import { AnimatePresence, motion } from 'framer-motion';
import { memo, useState, useEffect, Fragment, useCallback } from 'react';

import {
  Text,
  Box,
  Flex,
  Modal,
  ModalOverlay,
  ModalContent,
  Button,
  ModalBody,
  HStack,
  useToken,
  ModalHeader,
  ModalFooter,
} from '@vimeo/bokeh';
import { ChevronDownSmall } from '@vimeo/bokeh-icons';

import { T } from '../../translations';
import { CommentGroupProps } from '../../types';
import { idFromUri } from '../../utils';
import { Comment } from '../Comment';
import { DeletedComment } from '../Comment/DeletedComment';
import { CommentsSkeleton } from '../CommentsSkeleton';
import { InputArea } from '../InputArea';

const REPLY_INDENT = '40px'; // Avatar width 32 + gap 8

const CommentGroupComponent = ({
  comment,
  highlightedCommentId,
  isPublic,
  addReply,
  editComment,
  editReply,
  deleteComment,
  deleteReply,
  isSubmitTextLoading = false,
  isDeleteLoading = false,
  deeplinkedCommentId,
  isMoreCommentsLoading = false,
  lastElementRef,
  onCopyClick,
  canAddComments = true,
  richCommentComponent,
  onOpenCommentOptionsClick,
  isUserProfileClickable,
  onUserProfileClick,
  replyError,
  editCommentError,
  editReplyError,
  onMomentPlay,
  toggleCommentStatus,
  canResolveComment,
  enableLinks,
  setPreviewClip,
  searchQuery,
  isGuestModalActive,
  isPreviousVersion = false,
  onReportClick,
  videoPrivacy,
  onAddReaction,
  onRemoveReaction,
}: CommentGroupProps) => {
  const [showReplies, setShowReplies] = useState(true);
  const [activeReplyId, setActiveReplyId] = useState('');
  const [activeEditId, setActiveEditId] = useState('');
  const [activeDeleteCommentId, setActiveDeleteCommentId] = useState('');
  const [activeDeleteReplyId, setActiveDeleteReplyId] = useState('');
  const [size200, size300] = useToken('space', ['200', '300']);
  const [draftText, setDraftText] = useState('');
  const hasReplies = comment.replies && comment.replies.length > 0;
  const isBeingRepliedTo = activeReplyId && activeReplyId === comment.id;
  const showReplyButton = canAddComments && !isBeingRepliedTo;
  const showCommentInput = isBeingRepliedTo && !hasReplies;
  const isActive = deeplinkedCommentId ? comment.id.includes(deeplinkedCommentId) : false;

  const isReplyActive = deeplinkedCommentId
    ? comment.replies?.some(
        (reply) => deeplinkedCommentId && reply.id.includes(deeplinkedCommentId),
      )
    : false;

  const onCancelClick = () => {
    if (activeReplyId) {
      setActiveReplyId('');
    }

    if (activeEditId) {
      setActiveEditId('');
    }
  };

  const onDeleteClick = (commentId: string) => {
    setActiveDeleteCommentId(commentId);
  };

  const onDeleteReplyClick = (commentId: string) => {
    setActiveDeleteReplyId(commentId);
  };

  const onDeleteCommentSubmit = () => {
    deleteComment(activeDeleteCommentId, Boolean(comment?.timeCode));
  };

  const clearActiveDeleteId = () => {
    setActiveDeleteCommentId('');
    setActiveDeleteReplyId('');
  };

  const onDeleteReplySubmit = () => {
    deleteReply(activeDeleteReplyId, Boolean(comment?.timeCode));
  };

  const onReplySubmit = (text: string) => {
    if (activeReplyId) {
      addReply(activeReplyId, text);
    }
  };

  const onEditSubmit = (text: string) => {
    if (activeEditId) {
      editComment(activeEditId, text);
    }
  };

  const onEditReplySubmit = (text: string) => {
    if (activeEditId) {
      editReply(activeEditId, text);
    }
  };

  useEffect(() => {
    if ((isActive || isReplyActive) && typeof comment?.timeCode === 'number') {
      onMomentPlay(comment.timeCode, comment);
    }
  }, [comment?.timeCode, isActive, onMomentPlay, isReplyActive, comment]);

  useEffect(() => {
    if (!isDeleteLoading && (activeDeleteCommentId || activeDeleteReplyId)) {
      clearActiveDeleteId();
    }
    // Need to exclude activeDeleteCommentId, activeDeleteReplyId from dependency array as we want this to run only when the api call is complete.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDeleteLoading]);

  const activeEditCommentErrorMessage =
    activeEditId && comment.id === editCommentError?.errorCommentId
      ? editCommentError.errorMessage
      : '';

  const activeReplyErrorMessage =
    activeReplyId && comment.id === replyError?.errorCommentId ? replyError.errorMessage : '';

  const activeError =
    (activeEditId && (editCommentError || editReplyError)) || (activeReplyId && replyError);

  const isDeletedComment = !!comment.deletedOn;

  useEffect(() => {
    if (!isSubmitTextLoading && !activeError) {
      onCancelClick(); // Reset the active reply/edit id when submit call has finished
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSubmitTextLoading]);

  const handleCommentsClick = useCallback(() => {
    if (comment?.timeCode) {
      onMomentPlay(comment.timeCode, comment);
    }
  }, [comment, onMomentPlay]);

  const replySpacing = {
    base: `calc(${size200} + ${REPLY_INDENT})`,
    md: `calc(${size300} + ${REPLY_INDENT})`,
  };

  const isCommentBeingEdited = activeEditId === comment.id;
  const isCommentHighlightedOnTimeline = highlightedCommentId === comment.id;

  return (
    <>
      <Flex
        ref={lastElementRef || undefined}
        key={comment.id}
        direction="column"
        backgroundColor={isActive || isReplyActive ? 'fill-component' : 'transparent'}
        _hover={{
          // Note: fill-secondary hasn't been added to bokeh yet
          backgroundColor: 'fill-secondary',
        }}
      >
        {isDeletedComment ? (
          <DeletedComment isDeeplinkedComment={isActive} />
        ) : (
          <Comment
            id={comment.id}
            isHighlightedOnTimeline={isCommentHighlightedOnTimeline}
            user={comment.user}
            createdTime={comment.createdTime}
            text={comment.text}
            timeCode={comment.timeCode}
            formattedTimeCode={comment.formattedTimeCode}
            coordinates={comment.coordinates}
            replies={comment.replies}
            isOwnComment={comment.isOwnComment}
            isOwnVideo={comment.isOwnVideo}
            activeReplyId={activeReplyId}
            editedTime={comment.editedTime}
            onDeleteClick={onDeleteClick}
            onCopyClick={onCopyClick}
            onEditClick={(commentId) => setActiveEditId(commentId)}
            onReplyClick={(commentId) => setActiveReplyId(commentId)}
            isDeeplinkedComment={isActive}
            isRichComment={comment.isRichComment}
            enableLinks={enableLinks}
            canAddComments={canAddComments}
            onOpenCommentOptionsClick={onOpenCommentOptionsClick}
            isUserProfileClickable={isUserProfileClickable}
            onUserProfileClick={onUserProfileClick}
            status={comment.status}
            toggleCommentStatus={toggleCommentStatus}
            canResolveComment={canResolveComment}
            textDecorations={comment.textDecorations}
            setPreviewClip={setPreviewClip}
            isBeingEdited={isCommentBeingEdited}
            onCommentsClick={handleCommentsClick}
            searchQuery={searchQuery}
            isPreviousVersion={isPreviousVersion}
            onReportClick={onReportClick}
            videoPrivacy={videoPrivacy}
            isPublic={isPublic}
            isUnread={comment.isUnread}
            reactions={comment.reactions}
            onAddReaction={onAddReaction}
            onRemoveReaction={onRemoveReaction}
          />
        )}

        {/* Below InputArea renders below parent comment on editing parent comment and for first reply */}
        {showCommentInput || isCommentBeingEdited ? (
          <Box
            ml={replySpacing}
            pr={{ base: 200, md: 300 }}
            pb={100}
            mt={activeEditId ? '-8px' : 0}
          >
            <InputArea
              isLoading={isSubmitTextLoading}
              isPublic={isPublic}
              autoFocus={true}
              onCancel={onCancelClick}
              onSubmit={activeEditId ? onEditSubmit : onReplySubmit}
              editingText={activeEditId ? comment.text : draftText}
              shouldShowCancel={isCommentBeingEdited}
              submitButtonText={activeEditId ? T.SaveEdit : T.SubmitComment}
              richCommentComponent={richCommentComponent}
              setDraftText={setDraftText}
              commentContextFields={{
                commentId: activeEditId ? idFromUri(activeEditId) : idFromUri(comment.id),
                isReply: !activeEditId,
                existingReplyCounts: comment.replies?.length || 0,
                isEdit: !!activeEditId,
                isTimeCoded: !!comment.timeCode,
              }}
              errorMessage={activeEditId ? activeEditCommentErrorMessage : activeReplyErrorMessage}
              isGuestModalActive={isGuestModalActive}
            />
          </Box>
        ) : null}

        {hasReplies ? (
          <HStack ml={replySpacing} spacing={50}>
            <Button
              onClick={() => setShowReplies((prevState) => !prevState)}
              size="xs"
              rightIcon={
                <ChevronDownSmall
                  transform={`rotate(${showReplies ? 180 : 0}deg)`}
                  transition="transform 0.3s ease"
                />
              }
              variant="tertiary"
            >
              {T.RepliesToggle(comment.replies?.length ?? 0)}
            </Button>
          </HStack>
        ) : null}

        <AnimatePresence>
          {showReplies && hasReplies ? (
            <Flex
              as={motion.div}
              direction="column"
              ml={replySpacing}
              borderLeft="1px solid"
              borderColor="stroke"
              data-testid="replies-container"
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto', transition: { type: 'tween' } }}
              exit={{ opacity: 0, height: 0 }}
              overflow="hidden"
            >
              {comment.replies?.map((reply, index) => {
                const isDeeplinkedReply = !!(
                  deeplinkedCommentId &&
                  reply.id.substring(reply.id.lastIndexOf('/') + 1).includes(deeplinkedCommentId)
                );
                const activeEditReplyErrorMessage =
                  reply.id === editReplyError?.errorCommentId ? editReplyError.errorMessage : ''; // Only show the error for input the corresponds to the error comment id
                const isReplyBeingEdited = activeEditId === reply.id;
                const isReplyHighlightedOnTimeline = highlightedCommentId === reply.id;

                const isLastComment = index === (comment.replies?.length ?? 0) - 1;
                const showReplyInput = isBeingRepliedTo && hasReplies && isLastComment;

                return (
                  <Fragment key={reply.id}>
                    <Comment
                      isDeeplinkedComment={isDeeplinkedReply}
                      isHighlightedOnTimeline={isReplyHighlightedOnTimeline}
                      id={reply.id}
                      user={reply.user}
                      createdTime={reply.createdTime}
                      text={reply.text}
                      coordinates={reply.coordinates}
                      isOwnComment={reply.isOwnComment}
                      isOwnVideo={reply.isOwnVideo}
                      editedTime={reply.editedTime}
                      isReply
                      isRichComment={reply.isRichComment}
                      enableLinks={enableLinks}
                      activeReplyId={activeReplyId}
                      onEditClick={(commentId) => setActiveEditId(commentId)}
                      onDeleteClick={onDeleteReplyClick}
                      onCopyClick={onCopyClick}
                      onOpenCommentOptionsClick={onOpenCommentOptionsClick}
                      isUserProfileClickable={isUserProfileClickable}
                      onUserProfileClick={onUserProfileClick}
                      status={comment.status}
                      canAddComments={canAddComments}
                      textDecorations={reply.textDecorations}
                      setPreviewClip={setPreviewClip}
                      isBeingEdited={isReplyBeingEdited}
                      onCommentsClick={handleCommentsClick}
                      parentCommentId={comment.id}
                      searchQuery={searchQuery}
                      isPreviousVersion={isPreviousVersion}
                      onReportClick={onReportClick}
                      videoPrivacy={videoPrivacy}
                      isPublic={isPublic}
                      reactions={reply.reactions}
                      onAddReaction={
                        onAddReaction
                          ? (replyId, code) => onAddReaction(replyId, code, true, comment.id)
                          : undefined
                      }
                      onRemoveReaction={
                        onRemoveReaction
                          ? (replyId, uri) => onRemoveReaction(replyId, uri, true, comment.id)
                          : undefined
                      }
                    />
                    {/* Below InputArea renders below replies for replies on thread and editing replies */}
                    {showReplyInput || isReplyBeingEdited ? (
                      <Box pl="8px" pr={{ base: 200, md: 300 }} pb={100}>
                        <InputArea
                          isLoading={isSubmitTextLoading}
                          isPublic={isPublic}
                          autoFocus={true}
                          onCancel={onCancelClick}
                          onSubmit={activeEditId ? onEditReplySubmit : onReplySubmit}
                          editingText={activeEditId ? reply.text : draftText}
                          setDraftText={setDraftText}
                          shouldShowCancel={isReplyBeingEdited}
                          submitButtonText={activeEditId ? T.SaveEdit : T.SubmitComment}
                          commentContextFields={{
                            commentId: idFromUri(activeReplyId) ?? idFromUri(comment.id),
                            isReply: true,
                            existingReplyCounts: comment.replies?.length || 0,
                            isEdit: !!activeEditId,
                          }}
                          richCommentComponent={richCommentComponent}
                          errorMessage={
                            activeEditId ? activeEditReplyErrorMessage : activeReplyErrorMessage
                          }
                          isGuestModalActive={isGuestModalActive}
                        />
                      </Box>
                    ) : null}
                  </Fragment>
                );
              })}
              {showReplyButton && !activeEditId && !isPreviousVersion && (
                <HStack>
                  <Button
                    variant="secondary"
                    size="xs"
                    mt="4px"
                    ml="63px"
                    mb="8px"
                    onClick={() => setActiveReplyId(comment.id)}
                  >
                    {T.Reply}
                  </Button>
                </HStack>
              )}
            </Flex>
          ) : null}
        </AnimatePresence>
        {isMoreCommentsLoading && <CommentsSkeleton />}
      </Flex>
      <Modal
        isOpen={!!activeDeleteCommentId || !!activeDeleteReplyId}
        onClose={clearActiveDeleteId}
        isCentered
      >
        <ModalOverlay />
        <ModalContent width="482px" maxWidth="calc(100vw - 32px)" height="180px">
          <ModalHeader>
            <Text variant="body-xl" textAlign="left" fontSize="20px">
              {T.ConfirmDelete}
            </Text>
          </ModalHeader>
          {/* Below modalbody adds the dynamic padding 56/28px based on screens */}
          <ModalBody />
          <ModalFooter>
            <HStack>
              <Button variant="tertiary" onClick={clearActiveDeleteId}>
                {T.Cancel}
              </Button>
              <Button
                isLoading={isDeleteLoading}
                variant="destructive"
                onClick={activeDeleteCommentId ? onDeleteCommentSubmit : onDeleteReplySubmit}
              >
                <span>{T.Delete}</span>
              </Button>
            </HStack>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};

export const CommentGroup = memo(CommentGroupComponent);
CommentGroup.displayName = 'CommentGroup';
